import React, { useState, useEffect } from 'react';
import { devError } from '@/lib/logger';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Clock, LogOut, ArrowLeft, Plus, Calendar, RefreshCw, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortalRequests() {
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    request_type: 'absence',
    description: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    checkAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkAuth = async () => {
    try {
      const res = await fetch(`${API}/portal/me`, { credentials: 'include' });
      if (!res.ok) {
        navigate('/portal');
        return;
      }
      const data = await res.json();
      setEmployee(data);
      fetchRequests(data.employee_id);
    } catch (err) { devError("op", err);
      navigate('/portal');
    }
  };

  const fetchRequests = async (employeeId) => {
    try {
      const res = await fetch(`${API}/requests?employee_id=${employeeId}`, { credentials: 'include' });
      const data = await res.json();
      setRequests(data);
    } catch (error) {
      toast.error('Erro ao carregar solicitações');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.description) {
      toast.error('Descrição é obrigatória');
      return;
    }

    try {
      const res = await fetch(`${API}/requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          employee_id: employee.employee_id,
          ...formData
        })
      });

      if (!res.ok) throw new Error('Erro ao enviar');
      
      toast.success('Solicitação enviada');
      setDialogOpen(false);
      setFormData({ request_type: 'absence', description: '', start_date: '', end_date: '' });
      fetchRequests(employee.employee_id);
    } catch (error) {
      toast.error('Erro ao enviar solicitação');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/portal/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (err) {
      devError('portal-logout', err);
    }
    navigate('/portal');
  };

  const requestTypeLabels = {
    vacation: 'Férias',
    shift_swap: 'Troca de Turno',
    absence: 'Aviso de Falta'
  };

  const requestTypeIcons = {
    vacation: <Calendar className="w-5 h-5" />,
    shift_swap: <RefreshCw className="w-5 h-5" />,
    absence: <AlertCircle className="w-5 h-5" />
  };

  const statusLabels = {
    pending: 'Pendente',
    approved: 'Aprovado',
    rejected: 'Rejeitado'
  };

  const statusColors = {
    pending: 'bg-[#F59E0B]',
    approved: 'bg-[#059669]',
    rejected: 'bg-[#DC2626]'
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="w-8 h-8 border-2 border-[#111111] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Toaster position="top-right" />
      
      {/* Header */}
      <header className="border-b border-[#E5E7EB] bg-white px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/portal/dashboard">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#111111] flex items-center justify-center">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-lg font-black tracking-tight block" style={{ fontFamily: 'Satoshi, sans-serif' }}>
                  Minhas Solicitações
                </span>
                <span className="text-sm text-[#6B7280]">{employee?.name}</span>
              </div>
            </div>
          </div>
          
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="portal-logout-btn">
            <LogOut className="w-4 h-4 mr-2" />
            Sair
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6">
        {/* New Request Button */}
        <div className="mb-6">
          <Button onClick={() => setDialogOpen(true)} data-testid="new-request-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nova Solicitação
          </Button>
        </div>

        {/* Requests List */}
        <div className="space-y-4">
          {requests.length === 0 ? (
            <div className="text-center py-16 border border-[#E5E7EB] bg-white">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-[#9CA3AF]" />
              <p className="text-[#6B7280]">Você ainda não fez nenhuma solicitação</p>
            </div>
          ) : (
            requests.map(req => (
              <div 
                key={req.request_id}
                className={`border border-[#E5E7EB] bg-white p-4 border-l-4 ${
                  req.status === 'pending' ? 'border-l-[#F59E0B]' :
                  req.status === 'approved' ? 'border-l-[#059669]' : 'border-l-[#DC2626]'
                }`}
                data-testid={`portal-request-${req.request_id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 flex items-center justify-center ${statusColors[req.status]} text-white`}>
                      {requestTypeIcons[req.request_type]}
                    </div>
                    <div>
                      <h3 className="font-semibold">{requestTypeLabels[req.request_type]}</h3>
                      <p className="text-xs text-[#6B7280]">
                        {new Date(req.created_at).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </div>
                  <span className={`badge ${statusColors[req.status]} text-white`}>
                    {statusLabels[req.status]}
                  </span>
                </div>

                <p className="text-sm mb-2">{req.description}</p>
                
                {(req.start_date || req.end_date) && (
                  <p className="text-sm text-[#6B7280]">
                    Período: {req.start_date || '-'} até {req.end_date || '-'}
                  </p>
                )}

                {req.response_note && (
                  <div className="mt-3 p-3 bg-[#F3F4F6]">
                    <p className="text-xs uppercase tracking-widest text-[#6B7280] mb-1">Resposta:</p>
                    <p className="text-sm">{req.response_note}</p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </main>

      {/* New Request Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Solicitação</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Tipo de Solicitação</Label>
              <Select 
                value={formData.request_type} 
                onValueChange={(v) => setFormData({ ...formData, request_type: v })}
              >
                <SelectTrigger data-testid="request-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="absence">Aviso de Falta</SelectItem>
                  <SelectItem value="vacation">Solicitação de Férias</SelectItem>
                  <SelectItem value="shift_swap">Troca de Turno</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Descrição *</Label>
              <Textarea
                data-testid="request-description-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descreva sua solicitação..."
                rows={4}
              />
            </div>

            {(formData.request_type === 'vacation' || formData.request_type === 'shift_swap') && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Data Início</Label>
                  <Input
                    data-testid="request-start-date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Data Fim</Label>
                  <Input
                    data-testid="request-end-date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} data-testid="submit-request-btn">Enviar Solicitação</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
